import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { Button, Form, FormGroup } from 'reactstrap';
import React, { Fragment, useCallback, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { DataPrivacyRequestAgentFieldSet } from 'site-modules/data-privacy-request/components/data-privacy-request-agent-field-set/data-privacy-request-agent-field-set';
import { DataPrivacyRequestConsumerFieldSet } from 'site-modules/data-privacy-request/components/data-privacy-request-consumer-field-set/data-privacy-request-consumer-field-set';
import {
  DATA_PRIVACY_FORM_FIELDS,
  IDENTITIES,
  IDENTITY_OPTIONS,
  PRIVACY_REQUESTS,
} from 'site-modules/data-privacy-request/constants/constants';
import { FormErrorMessage } from 'site-modules/data-privacy-request/components/form-error-message/form-error-message';
import { DataPrivacyFormField } from 'site-modules/data-privacy-request/components/data-privacy-form-field/data-privacy-form-field';
import { DataPrivacyCheckbox } from 'site-modules/data-privacy-request/components/data-privacy-checkbox/data-privacy-checkbox';
import { submitDataPrivacyRequest } from 'site-modules/data-privacy-request/utils/data-privacy-request-service';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { DataPrivacyRequestContent } from 'site-modules/data-privacy-request/components/data-privacy-request-content/data-privacy-request-content';
import { CmsEntities } from 'client/data/models/cms';
import {
  agentsDescriptionSelector,
  optOutOfSharingSelector,
  agentsNoteSelector,
  businessAffiliationConsumerSelector,
  businessAffiliationAgentSelector,
  consumerDescriptionSelector,
  employmentAffiliationConsumerSelector,
  employmentAffiliationAgentSelector,
} from 'site-modules/data-privacy-request/utils/cms-content-selectors';
import { connect } from 'react-redux';

import './data-privacy-request-form.scss';

const transformValues = fields =>
  Object.fromEntries(Object.entries(fields).map(([fieldName, field]) => [fieldName, field.value()]));

const getIdentityDescription = (identity, cmsContent) => {
  const identityDescriptionMap = {
    [IDENTITIES.CONSUMER]: consumerDescriptionSelector(cmsContent),
    [IDENTITIES.AUTHORIZED_AGENT]: agentsDescriptionSelector(cmsContent),
  };

  return identityDescriptionMap[identity];
};
const CHECKBOXES_LEGEND = {
  [IDENTITIES.AUTHORIZED_AGENT]: "Consumer's association with Edmunds",
  [IDENTITIES.CONSUMER]: 'Your association with Edmunds',
};

const OptOutButton = ({ onClick, isPending = false }) => (
  <Fragment>
    <Button type="button" onClick={onClick} className="opt-out-button" color="success" disabled={isPending}>
      {isPending ? <Spinner size={17} thickness={3} color="white" /> : 'OPT OUT'}
    </Button>
    {isPending && <span className="visually-hidden">Loading</span>}
  </Fragment>
);

export const mapStateToProps = state => ({
  skip: !!get(state, 'featureFlags.data-privacy-skip-param'),
  isAuthenticated: state?.profile?.isAuthenticated,
});

const DataPrivacyRequestFormUI = ({
  onSubmitSuccess,
  stateCodes,
  validationErrors = {},
  visitorId,
  visitorPrivacy,
  fieldRef,
  validate,
  fields = {},
  setModelValue,
  cmsContent,
  skip,
  isAuthenticated = false,
}) => {
  const [isSubmitPending, setSubmitPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [identity, setIdentity] = useState();
  const [optOutStatus, setOptOutStatus] = useState(null);

  const handleOptOutClick = useCallback(async () => {
    setOptOutStatus('pending');
    try {
      await submitDataPrivacyRequest({
        fields: {
          [DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE]: PRIVACY_REQUESTS.CCPA_OPT_OUT_SALE_PI,
          uuid: visitorId,
          ...(skip ? { skip } : {}),
        },
        setModelValue,
        isAuthenticated,
      });
      setOptOutStatus('success');
    } catch {
      setOptOutStatus('error');
    }
  }, [setModelValue, visitorId, skip, isAuthenticated]);

  let optOutButtonDisplay;
  if (optOutStatus === 'success' || visitorPrivacy.doNotSell === true) {
    optOutButtonDisplay = (
      <p
        style={{ fontWeight: 'bold', marginTop: '10px' }}
        className="d-flex align-items-center text-green-50"
        data-test="opt-out-thank-you"
      >
        <span className="icon-checkmark-circle me-0_75" aria-hidden />
        <span className="visually-hidden">Success:</span>
        You have successfully opted out of the sale and sharing of your Personal Information.
      </p>
    );
  } else if (optOutStatus === 'error') {
    optOutButtonDisplay = (
      <p style={{ fontWeight: 'bold', marginTop: '10px' }} className="d-flex align-items-center text-red-50">
        <span className="icon-warning2 me-0_75" aria-hidden />
        <span className="visually-hidden">Error:</span>
        There was an error processing your opt-out request. Please try again.
      </p>
    );
  } else {
    optOutButtonDisplay = <OptOutButton onClick={handleOptOutClick} isPending={optOutStatus === 'pending'} />;
  }

  const validateNames = useCallback(() => {
    const {
      afn: agentFirstName,
      aln: agentLastName,
      ufn: consumerFirstName,
      uln: consumerLastName,
    } = transformValues(fields);
    return !(agentFirstName === consumerFirstName && agentLastName === consumerLastName);
  }, [fields]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      setSubmitError(null);

      if (!validate()) {
        return;
      }

      if (identity === IDENTITIES.AUTHORIZED_AGENT && !validateNames()) {
        setSubmitError('Agent name and Consumer name cannot be identical. Please correct name(s).');
        return;
      }

      try {
        setSubmitPending(true);
        const transformedFields = transformValues(fields);
        await submitDataPrivacyRequest({
          fields: {
            ...transformedFields,
            uuid: visitorId,
            ...(skip ? { skip } : {}),
          },
          setModelValue,
        });

        setSubmitPending(false);
        onSubmitSuccess();
      } catch (error) {
        const errorMessage =
          get(error, 'custom.Message') === 'Invalid Phone number'
            ? 'Please verify the phone number is correct.'
            : 'Sorry, something went wrong, please try again.';
        setSubmitError(errorMessage);
        setSubmitPending(false);
      }
    },
    [fields, identity, onSubmitSuccess, setModelValue, validate, validateNames, visitorId, skip]
  );

  const handleFormChange = useCallback(() => {
    if (submitError) {
      setSubmitError(null);
    }
  }, [submitError]);

  const handleIdentityChange = useCallback(selectedIdentity => {
    setIdentity(selectedIdentity.value);
  }, []);

  return (
    <Form
      className="data-privacy-request-form px-1 px-md-0"
      noValidate
      onSubmit={handleSubmit}
      onChange={handleFormChange}
    >
      <FormGroup className="form-group" noMargin>
        <Fragment>
          <DataPrivacyRequestContent className="mb-1">{optOutOfSharingSelector(cmsContent)}</DataPrivacyRequestContent>
          <div aria-live="polite" aria-atomic="true">
            {optOutButtonDisplay}
          </div>
          <Fragment>
            <hr />
          </Fragment>
        </Fragment>
        <DataPrivacyRequestContent className="mb-1 mt-1">{agentsNoteSelector(cmsContent)}</DataPrivacyRequestContent>
        <DataPrivacyFormField
          value={identity}
          options={IDENTITY_OPTIONS}
          isFirstOptionDisabled
          onChange={handleIdentityChange}
          id="identity"
          name={DATA_PRIVACY_FORM_FIELDS.IDENTITY}
          valueKey="value"
          labelKey="label"
          labelText="I am a (an)"
          labelClassName="ps-0_25"
          toggle="Select an option"
          fieldRef={fieldRef}
          tag={StyledSelect}
          required
          className="identity-select"
          formControlColor="text-gray-darkest"
        />
      </FormGroup>
      <DataPrivacyRequestContent className="mt-1 mb-1">
        {getIdentityDescription(identity, cmsContent)}
      </DataPrivacyRequestContent>
      <fieldset>
        <legend className="visually-hidden">
          {identity ? CHECKBOXES_LEGEND[identity] : 'Association with Edmunds'}
        </legend>
        <FormGroup className="form-group" noMargin>
          <DataPrivacyFormField
            id={DATA_PRIVACY_FORM_FIELDS.IS_EMPLOYMENT_AFFILIATION}
            labelText="Current or former Edmunds job applicants, employees or contractors - California residents"
            name={DATA_PRIVACY_FORM_FIELDS.IS_EMPLOYMENT_AFFILIATION}
            containerClassName="checkbox-container"
            fieldRef={fieldRef}
            type="checkbox"
            tag={DataPrivacyCheckbox}
            aria-describedby={`${DATA_PRIVACY_FORM_FIELDS.IS_EMPLOYMENT_AFFILIATION}Description`}
          />
          <DataPrivacyRequestContent
            className="ps-1_25"
            id={`${DATA_PRIVACY_FORM_FIELDS.IS_EMPLOYMENT_AFFILIATION}Description`}
          >
            {identity === IDENTITIES.AUTHORIZED_AGENT
              ? employmentAffiliationAgentSelector(cmsContent)
              : employmentAffiliationConsumerSelector(cmsContent)}
          </DataPrivacyRequestContent>
        </FormGroup>
        <FormGroup className="form-group" noMargin>
          <DataPrivacyFormField
            id={DATA_PRIVACY_FORM_FIELDS.IS_BUSINESS_AFFILIATION}
            labelText="Business representatives - California Residents"
            name={DATA_PRIVACY_FORM_FIELDS.IS_BUSINESS_AFFILIATION}
            containerClassName="checkbox-container"
            fieldRef={fieldRef}
            type="checkbox"
            tag={DataPrivacyCheckbox}
            aria-describedby={`${DATA_PRIVACY_FORM_FIELDS.IS_BUSINESS_AFFILIATION}Description`}
          />
          <DataPrivacyRequestContent
            className="ps-1_25"
            id={`${DATA_PRIVACY_FORM_FIELDS.IS_EMPLOYMENT_AFFILIATION}Description`}
          >
            {identity === IDENTITIES.AUTHORIZED_AGENT
              ? businessAffiliationAgentSelector(cmsContent)
              : businessAffiliationConsumerSelector(cmsContent)}
          </DataPrivacyRequestContent>
        </FormGroup>
      </fieldset>
      {!!identity && (
        <Fragment>
          {identity === IDENTITIES.AUTHORIZED_AGENT && (
            <DataPrivacyRequestAgentFieldSet
              validationErrors={validationErrors}
              fieldRef={fieldRef}
              onValidateField={validate}
              stateCodes={stateCodes}
            />
          )}
          <DataPrivacyRequestConsumerFieldSet
            identity={identity}
            fieldRef={fieldRef}
            validationErrors={validationErrors}
            onValidate={validate}
            cmsContent={cmsContent}
            stateCodes={stateCodes}
          />
          {!!submitError && <FormErrorMessage className="mb-0_75" message={submitError} />}
          <Button type="submit" color="success" className="submit-button" disabled={!isEmpty(validationErrors)}>
            {isSubmitPending ? <Spinner color="white" size={18} /> : 'Submit'}
          </Button>
          <input type="hidden" id="uuid" name="uuid" value={visitorId} />
          <hr />
        </Fragment>
      )}
    </Form>
  );
};

OptOutButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
};

DataPrivacyRequestFormUI.propTypes = {
  onSubmitSuccess: PropTypes.func.isRequired,
  stateCodes: PropTypes.arrayOf(PropTypes.shape({ stateCode: PropTypes.string })).isRequired,
  validationErrors: PropTypes.shape({}),
  visitorId: PropTypes.string.isRequired,
  visitorPrivacy: PropTypes.shape({
    doNotSell: PropTypes.string,
  }).isRequired,
  fieldRef: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  fields: PropTypes.shape({}),
  setModelValue: PropTypes.func.isRequired,
  cmsContent: CmsEntities.Content.isRequired,
  skip: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool,
};

export const validators = {
  [DATA_PRIVACY_FORM_FIELDS.REQUEST_TYPE]: {
    test: validation.validateValue,
    errorText: 'Please select request type.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_STATE]: {
    test: validation.validateValue,
    errorText: 'Please select state.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_EMAIL]: {
    test: validation.validateEmail,
    errorText: 'Please enter your correct email address.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_FIRST_NAME]: {
    test: validation.validateName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_LAST_NAME]: {
    test: validation.validateName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_PHONE]: {
    test: validation.validatePhoneNumber,
    errorText: 'Please enter a valid phone number.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_ADDRESS]: {
    test: validation.validateAddress,
    errorText: 'Please enter a valid street address.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_CITY]: {
    test: validation.validateCity,
    errorText: 'Please enter a valid city name.',
  },
  [DATA_PRIVACY_FORM_FIELDS.CONSUMER_ZIP]: {
    test: validation.validateZip,
    errorText: 'Please enter a valid zip code.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_EMAIL]: {
    test: validation.validateEmail,
    errorText: 'Please enter your correct email address.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_FIRST_NAME]: {
    test: validation.validateName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_LAST_NAME]: {
    test: validation.validateName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_PHONE]: {
    test: validation.validatePhoneNumber,
    errorText: 'Please enter a valid phone number.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_ADDRESS]: {
    test: validation.validateAddress,
    errorText: 'Please enter a valid street address.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_CITY]: {
    test: validation.validateCity,
    errorText: 'Please enter a valid city name.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_ZIP]: {
    test: validation.validateZip,
    errorText: 'Please enter a valid zip code.',
  },
  [DATA_PRIVACY_FORM_FIELDS.AGENT_STATE]: {
    test: validation.validateValue,
    errorText: 'Please select state.',
  },
  [DATA_PRIVACY_FORM_FIELDS.REQUEST_DESCRIPTION]: {
    test: validation.validateValue,
    errorText: 'Please enter a description.',
  },
};

export const DataPrivacyRequestForm = formValidation(
  connect(mapStateToProps)(memo(DataPrivacyRequestFormUI)),
  validators
);
